import { normalizeMermaidSource } from '@/lib/integrationSuiteArtifact';
import { cn } from '@/lib/utils';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

let mermaidInitDone = false;
let mermaidRenderSeq = 0;

async function renderMermaid(uniqueId, source) {
  const mermaid = (await import('mermaid')).default;
  if (!mermaidInitDone) {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'neutral',
      securityLevel: 'loose',
      flowchart: { htmlLabels: true, curve: 'basis' },
    });
    mermaidInitDone = true;
  }
  const diagram = normalizeMermaidSource(source);
  const { svg } = await mermaid.render(uniqueId, diagram);
  return svg;
}

const DEFAULT_ZOOM = 1.35;
const MIN_ZOOM = 0.75;
const MAX_ZOOM = 2.25;

export default function IntegrationSuiteMermaid({ content, className }) {
  const reactId = useId().replace(/:/g, '');
  const containerRef = useRef(null);
  const [svgMarkup, setSvgMarkup] = useState('');
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  const text = String(content || '').trim();

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setSvgMarkup('');

    if (!text) return undefined;

    const run = async () => {
      const renderId = `is-mermaid-${reactId}-${++mermaidRenderSeq}`;
      try {
        const svg = await renderMermaid(renderId, text);
        if (!cancelled) setSvgMarkup(svg);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not render diagram');
        }
      } finally {
        document.getElementById(renderId)?.remove();
        document.getElementById(`d${renderId}`)?.remove();
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [text, reactId]);

  if (!text) {
    return (
      <div className="rounded-lg border border-cp-line bg-cp-inset p-4 text-sm text-cp-muted">—</div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-cp-mod">Diagram could not be rendered — showing source.</p>
        <pre className="overflow-auto whitespace-pre-wrap break-words rounded-lg border border-cp-line bg-cp-inset p-4 font-mono text-xs text-cp-fg">
          {text}
        </pre>
      </div>
    );
  }

  return (
    <div className={cn('flex min-h-0 flex-col rounded-lg border border-cp-line bg-white', className)}>
      {svgMarkup ? (
        <div className="flex shrink-0 items-center justify-end gap-1 border-b border-cp-line px-3 py-2">
          <button
            type="button"
            aria-label="Zoom out diagram"
            className="rounded border border-cp-line p-1 text-cp-muted hover:text-cp-fg"
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, Math.round((z - 0.15) * 100) / 100))}
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <span className="min-w-[3rem] text-center text-[10px] tabular-nums text-cp-muted">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            aria-label="Zoom in diagram"
            className="rounded border border-cp-line p-1 text-cp-muted hover:text-cp-fg"
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, Math.round((z + 0.15) * 100) / 100))}
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}
      <div
        ref={containerRef}
        className="min-h-[12rem] flex-1 overflow-auto overscroll-contain p-4"
      >
        {error ? null : !svgMarkup ? (
          <p className="text-xs text-muted-foreground animate-pulse">Rendering flow diagram…</p>
        ) : (
          <div
            className="inline-block min-w-max [&_svg]:block [&_svg]:h-auto [&_svg]:max-w-none"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
            }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: svgMarkup }}
          />
        )}
      </div>
    </div>
  );
}
